







































































































import Toast from '@/shared/services/toast';
import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { validationMixin } from 'vuelidate';
import { maxLength, required, requiredIf, helpers } from 'vuelidate/lib/validators';
import { Getter } from 'vuex-class';
import { GovNotifyServiceViewModel } from '../models/govNotifyServiceViewModel';
import { govNotifyApi } from '../services/govNotifyApi';

const numericIf = (fn: any) =>
	helpers.withParams({ type: 'numeric' }, (value: any, vm: any) => {
		if (!helpers.req(value)) {
			return true;
		}
		if (fn.call(this, vm)) {
			const regex = /^[0-9]+$/;
			return regex.test(value);
		}
		return true;
	});

@Component({
	mixins: [validationMixin],
	validations: {
		serviceData: {
			$each: {
				name: { required },
				phoneNumber: {
					required: requiredIf(vm => {
						return vm.isDraft;
					}),
					numeric: numericIf((vm: any) => {
						return vm.isDraft;
					}),
					maxLength: maxLength(15)
				},
				notifyApiKey: {
					required: requiredIf(vm => {
						return vm.isDraft;
					})
				},
				selectedTemplate: {
					required: requiredIf(vm => {
						return vm.isDraft;
					})
				}
			}
		}
	}
})
export default class GovNotifyServiceConfiguration extends Vue {
	@Getter public currentWorkspace!: WorkspaceViewModel;

	@Prop({ default: () => [] }) public value!: GovNotifyServiceViewModel[];

	@Prop() public loading!: boolean;

	public messageCallbackUrl: string = govNotifyApi.getMessageWebhookUrl();

	public deliveryCallbackUrl: string = govNotifyApi.getDeliveryWebhookUrl();

	public loadingToken = false;

	public loadingTemplates = false;

	public serviceToDelete: GovNotifyServiceViewModel | null = null;

	public serviceDataTableColumns = [
		{ name: 'name', display: 'Name' },
		{ name: 'phoneNumber', display: 'Phone Number' },
		{ name: 'notifyApiKey', display: 'API Key' },
		{ name: 'templateName', display: 'Template' },
		{ name: 'bearerToken', display: 'Bearer Token' }
	];

	public serviceData: Array<Partial<GovNotifyServiceViewModel>> = [];

	public hasInitialised = false;

	@Watch('value', { immediate: true })
	public onValueChange() {
		this.serviceData = this.value;
		if (this.value.length === 0 && !this.loading && !this.hasInitialised) {
			this.addEmptyRow();
		}
		if (this.value.length > 0) {
			this.hasInitialised = true;
		}
		this.$emit('error', this.$v.$invalid);
	}

	public async addEmptyRow() {
		this.loadingToken = true;
		try {
			const newService = {
				bearerToken: 'Loading...',
				notifyApiKey: '',
				name: '',
				phoneNumber: '',
				id: '',
				isDraft: true
			};
			this.serviceData.push(newService);

			const { bearerToken } = await govNotifyApi.getBearerToken(this.currentWorkspace.id);

			newService.bearerToken = bearerToken;
			newService.id = this.getServiceIdFromBearerToken(bearerToken);

			this.$emit('input', this.serviceData);
		} catch (err) {
			Toast.showError(err.message);
		}
		this.loadingToken = false;
	}

	public async loadTemplates(service: GovNotifyServiceViewModel) {
		if (service.notifyApiKey === '') {
			// If the field is empty, don't fetch templates.
			return;
		}
		this.loadingTemplates = true;
		try {
			const response = await govNotifyApi.getTemplates(this.currentWorkspace.id, service.notifyApiKey);

			service.templates = response.templates;
		} catch (err) {
			Toast.showError(err.message);
		}
		this.loadingTemplates = false;
	}

	public getTemplateOptions(service: GovNotifyServiceViewModel) {
		if (!service.templates) {
			return [{ label: 'No templates configured', value: null }];
		}
		return service.templates.map(template => ({ label: template.name, value: template }));
	}

	public getRowIndex(row: GovNotifyServiceViewModel) {
		return this.serviceData.indexOf(row);
	}

	public onChange() {
		this.$emit('error', this.$v.$invalid);
		this.$emit('input', this.serviceData);
	}

	public onCopy() {
		Toast.showInfo('Copied to clipboard');
	}

	public canLoadTemplates(row: GovNotifyServiceViewModel) {
		return !this.loadingTemplates && row.notifyApiKey.length > 0;
	}

	public getErrorMessage(row: GovNotifyServiceViewModel, field: string, label: string): string {
		const currentField = (this.$v.serviceData.$each as any)[this.getRowIndex(row)][field];
		if (!currentField.required && currentField.$dirty) {
			return `${label} is required`;
		}
		if (!currentField.numeric && currentField.$params.numeric && currentField.$dirty) {
			return `${label} must be numeric`;
		}
		if (!currentField.maxLength && currentField.$params.maxLength && currentField.$dirty) {
			return `${label} must not be longer than ${currentField.$params.maxLength.max} characters`;
		}
		return '';
	}

	public markAsTouched(row: GovNotifyServiceViewModel, field: string) {
		const currentField = (this.$v.serviceData.$each as any)[this.getRowIndex(row)][field];
		currentField.$touch();
	}

	public handleDeleteClick(row: GovNotifyServiceViewModel) {
		this.serviceToDelete = row;
		this.deleteService();
	}

	public deleteService() {
		if (this.serviceToDelete) {
			this.serviceData = this.serviceData.filter(service => service !== this.serviceToDelete);
			this.$emit('input', this.serviceData);
		}
	}

	public getServiceIdFromBearerToken(bearerToken: string) {
		const claims = JSON.parse(atob(bearerToken.split('.')[1]));
		return claims.serviceId;
	}
}
